/**
 * Class composition — the only way components build class strings.
 *
 * `clsx` for conditional joining, `tailwind-merge` for last-one-wins conflict
 * resolution. tailwind-merge cannot read `tokens.css`, so the bridged token
 * utilities are declared below; without that, `cn('text-muted', 'text-primary')`
 * would emit *both* classes and let stylesheet order pick the winner.
 */

import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/** Every colour name exposed by the `@theme inline` block in `tokens.css`. */
const TOKEN_COLORS: string[] = [
  'canvas',
  'surface-1',
  'surface-2',
  'surface-3',
  'surface-inset',
  'surface-overlay',
  'hover',
  'active',
  'selected',
  'line-subtle',
  'line',
  'line-strong',
  'track',
  'primary',
  'secondary',
  'muted',
  'faint',
  'inverse',
  'on-accent',
  'accent',
  'accent-hover',
  'accent-soft',
  'accent-line',
  'accent-text',
  'focus',
  'info',
  'info-soft',
  'success',
  'success-soft',
  'warning',
  'warning-soft',
  'danger',
  'danger-soft',
  'hot',
  'hot-soft',
  'warm',
  'warm-soft',
  'early',
  'early-soft',
  'cold',
  'cold-soft',
  'viz-1',
  'viz-2',
  'viz-3',
  'viz-4',
  'viz-5',
  'viz-6',
  'viz-7',
  'viz-8',
];

/** The dense-UI type ramp. `md` in particular is not a stock Tailwind size. */
const TOKEN_FONT_SIZES: string[] = [
  '2xs',
  'xs',
  'sm',
  'base',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
];

/** Elevation ramp: `shadow-1` … `shadow-4`. */
const TOKEN_SHADOWS: string[] = ['1', '2', '3', '4'];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: TOKEN_FONT_SIZES }],
      'text-color': [{ text: TOKEN_COLORS }],
      'bg-color': [{ bg: TOKEN_COLORS }],
      'border-color': [{ border: TOKEN_COLORS }],
      'divide-color': [{ divide: TOKEN_COLORS }],
      'ring-color': [{ ring: TOKEN_COLORS }],
      'outline-color': [{ outline: TOKEN_COLORS }],
      'caret-color': [{ caret: TOKEN_COLORS }],
      fill: [{ fill: TOKEN_COLORS }],
      stroke: [{ stroke: TOKEN_COLORS }],
      shadow: [{ shadow: TOKEN_SHADOWS }],
    },
  },
});

/** Join class values and resolve Tailwind conflicts (later wins). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export type { ClassValue };
